import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { Card } from '@neo/primitives/card'
import { useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, Navigate } from 'router-v6'
import { RECAPTCHA_SITE_KEY } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { z } from 'zod'
import { AuthLayout } from '../../shared/layouts/auth'
import {
  AUTH_INPUT_CLASS,
  AUTH_LINK_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
} from './components/auth-styles'
import { useForgotPasswordMutationV2 } from './queries'

interface ForgotPasswordFormData {
  email: string
}

const fields: GenericFieldConfig<ForgotPasswordFormData> = [
  {
    name: 'email',
    componentType: 'input',
    zodValidation: z.string().email('Invalid email').min(1, 'Email is required'),
    label: 'Registered Email',
    inputProps: {
      placeholder: 'jane@company.com',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'username',
      'data-testid': 'input-forgot-password-v2-email',
    },
  },
]

export const ForgotPasswordV2 = () => {
  const { userAuthenticated } = useAuth()
  const recaptchaRef = useRef() as React.MutableRefObject<ReCAPTCHA>
  const { isPending, mutate: onSubmit, error } = useForgotPasswordMutationV2(recaptchaRef)
  const defaultValueRef = useRef<ForgotPasswordFormData>({
    email: '',
  })
  const [resetEmail, setResetEmail] = useState<string | null>(null)

  if (userAuthenticated) return <Navigate to={getDefaultRoute()} />

  if (resetEmail) {
    return (
      <AuthLayout
        pageTitle="Reset Password"
        title="Check Your Email"
        description="We've sent a password reset link to your email address."
        footer={
          <div className="text-center text-p-400 text-secondary">
            Remember your password?{' '}
            <Link to="/login" className={AUTH_LINK_CLASS}>
              Login
            </Link>
          </div>
        }
      >
        <Card className="p-6 text-center">
          <p className="text-p-400">
            A password reset link has been sent to{' '}
            <span className="font-medium text-primary">{resetEmail}</span>.
          </p>
          <p className="mt-2 text-p-400 text-muted-foreground">
            Please check your inbox to reset your password.
          </p>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      pageTitle="Reset password"
      title="Forgot your password?"
      description="Enter your email to securely reset your password."
      footer={
        <div className="text-center text-p-400 text-secondary">
          Remember your password?{' '}
          <Link to="/login" className={AUTH_LINK_CLASS}>
            Login
          </Link>
        </div>
      }
    >
      {error && <Alert variant="destructive" content={getErrorText(error)} />}
      <GenericForm
        id="forgot-password-v2-form"
        defaultValues={defaultValueRef.current}
        fields={fields}
        onSubmit={(value) => {
          onSubmit(value, {
            onSuccess: () => setResetEmail(value.email),
          })
        }}
        className="grid gap-4"
        isSubmitting={isPending}
        submitText="Reset Password"
        includeSubmitButton={false}
      />
      <FormSubmitButton
        formId="forgot-password-v2-form"
        typography="action"
        className={AUTH_PRIMARY_BUTTON_CLASS}
        size="xl"
        loading={isPending}
      >
        Request Password Reset
      </FormSubmitButton>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
    </AuthLayout>
  )
}
